import { useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import MDTypography from "components/MDTypography";

import { useAuthFlow } from "layouts/authentication/shared/useAuthFlow";
import AuthShell from "layouts/authentication/shared/AuthShell";
import AuthForm from "layouts/authentication/shared/AuthForm";

import { GET_CONNECT_LINK_INFO, IConnectLinkInfo } from "graphql/queries/getConnectLinkInfo";
import { CONNECT_PROFILE_VIA_LINK } from "graphql/mutations/connectProfileViaLink";
import { brandName } from "constants/index";

// Chat → email magic-link connect. Reuses the exact login UI + state machine
// (AuthShell + AuthForm + useAuthFlow); the only differences are: the email is
// prefilled + locked from the link, the flow auto-starts, and after a successful
// authentication the channel profile is attached and the user is redirected into
// the dashboard (auto-redirect).
function Connect(): JSX.Element {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const { data, loading: infoLoading } = useQuery<{ getConnectLinkInfo: IConnectLinkInfo }>(
    GET_CONNECT_LINK_INFO,
    { variables: { token }, skip: !token, fetchPolicy: "network-only" }
  );
  const info = data?.getConnectLinkInfo;
  const email = info?.email || "";
  const channel = info?.channel || "";

  const [connectProfile] = useMutation(CONNECT_PROFILE_VIA_LINK);

  // Fires after any successful auth: attach the profile, then redirect by role.
  const onAuthenticated = async (role: string | null) => {
    try {
      const res = await connectProfile({ variables: { token } });
      if (res?.data?.connectProfileViaLink?.connected) {
        toast.success(`${channel || "Channel"} connected to ${email}`);
        navigate(role === "ADMIN" ? "/orders" : "/");
      } else {
        toast.error("Could not connect this channel");
      }
    } catch (err: any) {
      toast.error(err?.message || "Could not connect this channel");
    }
  };

  const flow = useAuthFlow({ lockedEmail: email, autoStart: true, onAuthenticated });

  const invalid = !token || (!infoLoading && !info?.valid);

  let body: JSX.Element | null;
  if (invalid) {
    body = (
      <MDTypography variant="body2" color="error" textAlign="center">
        This connect link is invalid or has expired. Please request a new one from your chat.
      </MDTypography>
    );
  } else if (infoLoading) {
    body = null; // overlay loader shows via AuthShell
  } else {
    body = <AuthForm flow={flow} />;
  }

  const title = invalid ? "Link unavailable" : flow.title;
  const subtitle = invalid
    ? ""
    : channel && !flow.showOtp && !flow.forgotPsw
    ? `Link your ${channel} chat to your ${brandName} account`
    : flow.subtitle;

  return (
    <AuthShell title={title} subtitle={subtitle} loading={infoLoading || flow.loading}>
      {body}
    </AuthShell>
  );
}

export default Connect;
